import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Edit2, Loader2, FolderTree } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SitesManagementPage = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    search_url: "",
    icon: ""
  });

  useEffect(() => {
    fetchSites();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.filter(cat => cat.is_active));
      // Set default category if available
      if (response.data.length > 0 && !formData.category) {
        setFormData(prev => ({ ...prev, category: response.data[0].name }));
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Erreur lors du chargement des catégories");
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`);
      setSites(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des sites");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.search_url) {
      toast.error("Veuillez remplir tous les champs requis");
      return;
    }

    try {
      if (editingSite) {
        await axios.patch(`${API}/sites/${editingSite.id}`, formData);
        toast.success("Site modifié avec succès");
      } else {
        await axios.post(`${API}/sites`, formData);
        toast.success("Site ajouté avec succès");
      }
      
      fetchSites();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const deleteSite = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce site ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/sites/${id}`);
      setSites(sites.filter(site => site.id !== id));
      toast.success("Site supprimé avec succès");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const toggleSiteActive = async (site) => {
    try {
      await axios.patch(`${API}/sites/${site.id}`, {
        is_active: !site.is_active
      });
      setSites(sites.map(s => 
        s.id === site.id ? { ...s, is_active: !s.is_active } : s
      ));
      toast.success(site.is_active ? "Site désactivé" : "Site activé");
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const openEditDialog = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      category: site.category,
      search_url: site.search_url,
      icon: site.icon || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSite(null);
    setFormData({
      name: "",
      category: "osint",
      search_url: "",
      icon: ""
    });
  };

  // Group sites by category dynamically
  const groupedSites = categories.reduce((acc, cat) => {
    acc[cat.name] = sites.filter(s => s.category === cat.name);
    return acc;
  }, {});

  // Create category labels dynamically
  const categoryLabels = categories.reduce((acc, cat) => {
    acc[cat.name] = cat.label;
    return acc;
  }, {});

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              onClick={() => navigate("/search")}
              variant="outline"
              className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-5xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Gestion des sites
              </h1>
              <p className="text-lg text-gray-400 mt-2">
                {sites.length} site(s) configuré(s)
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="manage-categories-btn"
              onClick={() => navigate("/categories")}
              variant="outline"
              className="bg-transparent border-purple-500/50 text-purple-400 hover:bg-purple-500/10 hover:border-purple-400"
            >
              <FolderTree className="w-4 h-4 mr-2" />
              Gérer les catégories
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-site-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un site
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gray-800 text-white">
              <DialogHeader>
                <DialogTitle data-testid="dialog-title">
                  {editingSite ? "Modifier le site" : "Ajouter un nouveau site"}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Configurez un nouveau site de recherche ou OSINT
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nom du site *</Label>
                  <Input
                    data-testid="site-name-input"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Ex: Google, Twitter, Shodan"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Catégorie *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="category-select" className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Sélectionnez une catégorie" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {categories.length > 0 ? (
                        categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.label}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="osint" disabled>
                          Chargement des catégories...
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="search_url">URL de recherche *</Label>
                  <Input
                    data-testid="search-url-input"
                    id="search_url"
                    value={formData.search_url}
                    onChange={(e) => setFormData({ ...formData, search_url: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="https://example.com/search?q={query}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Utilisez {'{query}'} comme placeholder pour la requête de recherche
                  </p>
                </div>
                <div>
                  <Label htmlFor="icon">Icône (emoji ou URL d&apos;image)</Label>
                  <Input
                    data-testid="icon-input"
                    id="icon"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="🔍 ou https://example.com/icon.png"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Emoji (ex: 🔍) ou URL d&apos;image (ex: https://google.com/favicon.ico)
                  </p>
                  {formData.icon?.startsWith('http') && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-400">Aperçu:</span>
                      <img 
                        src={formData.icon} 
                        alt="Aperçu" 
                        className="w-6 h-6 object-contain rounded"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
                <Button
                  data-testid="submit-site-btn"
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {editingSite ? "Modifier" : "Ajouter"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Sites List */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedSites).map(([category, categorySites]) => (
              <div key={category} className="glass rounded-2xl p-6 slide-in">
                <h2 className="text-2xl font-bold mb-4">
                  {categoryLabels[category]} ({categorySites.length})
                </h2>
                <div className="space-y-3">
                  {categorySites.map((site) => (
                    <div
                      key={site.id}
                      data-testid={`site-card-${site.id}`}
                      className="flex items-center justify-between p-4 rounded-lg bg-gray-900/30 border border-gray-800 hover:border-gray-700 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        {site.icon?.startsWith('http') ? (
                          <img src={site.icon} alt="" className="w-8 h-8 object-contain rounded" />
                        ) : (
                          <span className="text-3xl">{site.icon}</span>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{site.name}</h3>
                          <p className="text-sm text-gray-400 truncate max-w-md">{site.search_url}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`active-${site.id}`} className="text-sm">
                            {site.is_active ? "Actif" : "Inactif"}
                          </Label>
                          <Switch
                            data-testid={`toggle-active-${site.id}`}
                            id={`active-${site.id}`}
                            checked={site.is_active}
                            onCheckedChange={() => toggleSiteActive(site)}
                          />
                        </div>
                        <Button
                          data-testid={`edit-site-${site.id}`}
                          onClick={() => openEditDialog(site)}
                          size="sm"
                          variant="outline"
                          className="bg-transparent border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-site-${site.id}`}
                          onClick={() => deleteSite(site.id)}
                          size="sm"
                          variant="destructive"
                          className="bg-red-600/20 hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SitesManagementPage;